import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import {
  findDisplayNextLevel,
  useEndTrainingSession,
  useTrainingAttempt,
  useTrainingAttemptMessages,
  useTrainingSimulatorHome,
  type TrainingNextLevel,
} from "@/lib/api/training-simulator/training-simulator.ts";
import { useTrainingCompletionPresentation } from "@/lib/hooks/training-simulator/use-training-completion-presentation.ts";
import { useTrainingSessionEvents } from "@/lib/hooks/training-simulator/use-training-session-events.ts";
import { useTrainingResumePrompt } from "@/lib/hooks/training-simulator/use-training-resume-prompt.ts";
import {
  useContinueToNextTrainingLevel,
  useEnterTrainingSession,
} from "@/lib/hooks/training-simulator/use-enter-training-session.ts";
import { WELCOME_STORAGE_KEY } from "@/lib/training-simulator/constants.ts";
import {
  PostHogEvents,
  useTrackEvent,
  useTrackPage,
} from "@/lib/posthog-events.ts";
import { TrainingSessionTopbar } from "./training-session-topbar.tsx";
import { TrainingWelcomeModal } from "./training-welcome-modal.tsx";
import { TrainingResumeModal } from "./training-resume-modal.tsx";
import { TrainingLevelContextModal } from "./training-level-context-modal.tsx";
import { TrainingLevelCompleteModal } from "./training-level-complete-modal.tsx";
import { TrainingDevPanel } from "../dev/training-dev-panel.tsx";
import { TrainingSessionWorkspace } from "./training-session-workspace.tsx";

interface TrainingSessionProps {
  attemptId: string;
}

function hasSeenWelcome(): boolean {
  if (typeof window === "undefined") return true;
  return window.localStorage.getItem(WELCOME_STORAGE_KEY) === "1";
}

export function TrainingSession({ attemptId }: TrainingSessionProps) {
  useTrackPage(PostHogEvents.PAGE_TRAINING_SESSION);
  const { track } = useTrackEvent();
  const navigate = useNavigate();

  const {
    data: attempt,
    isLoading: isAttemptLoading,
    isError: isAttemptError,
  } = useTrainingAttempt(attemptId);
  const { data: transcript } = useTrainingAttemptMessages(attemptId);
  const { replyFailedThreadIds, resetReplyFailed } =
    useTrainingSessionEvents(attemptId);
  const { mutate: endSession, isPending: isEnding } =
    useEndTrainingSession(attemptId);

  const { data: home } = useTrainingSimulatorHome();

  const [welcomeAcknowledged, setWelcomeAcknowledged] = useState(hasSeenWelcome);
  const [contextAcknowledged, setContextAcknowledged] = useState(false);

  const [completionDismissed, setCompletionDismissed] = useState(false);
  const [replyFailedPreview, setReplyFailedPreview] = useState(false);

  const { enterSession, startingMode } = useEnterTrainingSession({
    onStarted: () => setCompletionDismissed(true),
  });
  const { continueToNextLevel, isContinuingToNextLevel } =
    useContinueToNextTrainingLevel({
      onStarted: () => setCompletionDismissed(true),
    });

  const handleWelcomeAcknowledge = () => {
    track(PostHogEvents.SIMULATOR_WELCOME_MODAL_DISMISS);
    window.localStorage.setItem(WELCOME_STORAGE_KEY, "1");
    setWelcomeAcknowledged(true);
  };

  const handleEndSession = () => {
    endSession(undefined, {
      onSuccess: () => {
        track(PostHogEvents.SIMULATOR_LEVEL_EXIT, { attempt_id: attemptId });
        navigate({ to: "/training" });
      },
    });
  };

  const isInProgress = attempt?.status === "in_progress";
  const { isCompleted, isFinalizing, showCompletion } =
    useTrainingCompletionPresentation({
      attemptId,
      attempt,
      transcript,
      completionDismissed,
    });

  const mentorMessageCount = useMemo(() => {
    if (attempt?.completion_result) {
      return attempt.completion_result.mentorMessageCount;
    }
    return (
      transcript?.reduce(
        (total, group) =>
          total +
          group.messages.filter((message) => message.sender_type === "mentor")
            .length,
        0,
      ) ?? 0
    );
  }, [attempt, transcript]);

  const resumePrompt = useTrainingResumePrompt({
    attemptId,
    attempt,
    mentorMessageCount,
    transcriptLoaded: transcript !== undefined,
    isCompleted,
    isSuppressed: !welcomeAcknowledged,
  });

  const showWelcome = !welcomeAcknowledged && !isCompleted;
  const showContext =
    welcomeAcknowledged &&
    resumePrompt.isResolved &&
    !contextAcknowledged &&
    !isCompleted;

  const nextLevel = useMemo<TrainingNextLevel | null>(() => {
    const match = findDisplayNextLevel(home, attempt?.level.id);
    if (!match) return null;
    return {
      id: match.level.id,
      position: match.level.position,
      title: match.level.title,
    };
  }, [attempt?.level.id, home]);

  if (isAttemptLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-surfaces-z3">
        <Loader2 className="size-10 animate-spin text-text-secondary" />
      </div>
    );
  }

  if (isAttemptError || !attempt) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-surfaces-z3">
        <p className="text-body-1 text-text-secondary">
          Something went wrong loading this session. Please try again.
        </p>
      </div>
    );
  }

  // Module/simulator completions are full-page screens that live in the app
  // shell (with the sidebar); only level completions stay as an in-session
  // modal. Routing is API-driven via `completion_screen`.
  if (
    isCompleted &&
    (attempt.completion_screen === "module" ||
      attempt.completion_screen === "simulator")
  ) {
    return (
      <Navigate
        to="/training/complete/$attemptId"
        params={{ attemptId }}
        replace
      />
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-surfaces-z3">
      <TrainingSessionTopbar
        attempt={attempt}
        mentorMessageCount={mentorMessageCount}
        onEndSession={handleEndSession}
        isEnding={isEnding}
      />

      <div className="flex min-h-0 flex-1 bg-surfaces-z3">
        <TrainingSessionWorkspace
          attemptId={attemptId}
          threads={attempt.threads}
          messageGroups={transcript}
          defaultThreadId={attempt.current_thread_id}
          isInProgress={isInProgress}
          isFinalizing={isFinalizing}
          replyFailedThreadIds={replyFailedThreadIds}
          replyFailedPreview={replyFailedPreview}
          onSend={resetReplyFailed}
        />
      </div>

      <TrainingWelcomeModal
        open={showWelcome}
        onAcknowledge={handleWelcomeAcknowledge}
      />
      <TrainingResumeModal {...resumePrompt.modal} />
      <TrainingLevelContextModal
        open={showContext}
        contextCopy={attempt.level.context_copy}
        onAcknowledge={() => {
          track(PostHogEvents.SIMULATOR_LEVEL_CONTEXT_MODAL_DISMISS, {
            module_index: attempt.level.module.position,
            level_index: attempt.level.position,
          });
          setContextAcknowledged(true);
        }}
      />
      <TrainingLevelCompleteModal
        open={showCompletion}
        levelPosition={attempt.level.position}
        modulePosition={attempt.level.module.position}
        moduleTitle={attempt.level.module.title}
        nextLevel={nextLevel}
        isContinuing={isContinuingToNextLevel}
        isRetrying={startingMode === "retry"}
        onContinue={continueToNextLevel}
        onRetry={() => enterSession(attempt.level.id, "retry")}
        onBackToTraining={() => {
          track(PostHogEvents.SIMULATOR_BACK_TO_TRAINING_CLICK, {
            source: "level_complete_modal",
          });
          setCompletionDismissed(true);
          navigate({ to: "/training" });
        }}
      />

      <TrainingDevPanel
        attempt={attempt}
        nextLevel={nextLevel}
        onToggleReplyFailed={() => setReplyFailedPreview((value) => !value)}
      />
    </div>
  );
}
